<template>
  <content-dialog
    :visible.sync="modalVisible"
    max-width="600px"
  >
    <!--<editor-fold desc="DialogTitle">-->
    <template slot="dialogTitle">
      <span v-if="ownerDirty.id">
        Edit Yacht Owner
      </span>
      <span v-else>
        Add New Yacht Owner
      </span>
    </template>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Content/Form">-->
    <v-card-text :class="{'pa-5': !$vuetify.breakpoint.xsOnly }">
      <v-subheader>Basic Details</v-subheader>
      <!--<editor-fold desc="Name">-->
      <v-text-field
        v-model="ownerDirty.name"
        v-validate="'required'"
        name="name"
        label="Name"
        outlined
        :error-messages="veeErrors.collect('name')"
      />
      <!--</editor-fold>-->

      <!--<editor-fold desc="Country">-->
      <v-autocomplete
        v-model="ownerDirty.country_id"
        v-validate="'required'"
        :loading="countries.loading"
        :items="countries.data"
        item-value="id"
        item-text="name"
        name="country_id"
        label="Country"
        outlined
        data-vv-as="country"
        :error-messages="veeErrors.collect('country_id')"
      />
      <!--</editor-fold>-->
      <v-switch
        v-model="ownerDirty.is_company"
        label="Yacht Owner represented by a company"
      />
      <div v-if="ownerDirty.is_company">
        <v-subheader>Company Details</v-subheader>
        <v-text-field
          v-model="ownerDirty.company_details.id"
          v-validate="'required'"
          name="company_vat_id"
          label="VAT ID"
          outlined
          data-vv-as="VAT ID"
          :error-messages="veeErrors.collect('company_vat_id')"
        />
        <v-text-field
          v-model="ownerDirty.company_details.name"
          v-validate="'required'"
          name="company_name"
          label="Company Name"
          outlined
          data-vv-as="company name"
          :error-messages="veeErrors.collect('company_name')"
        />
      </div>

      <div v-if="ownerDirty.contact_details.investor_contact">
        <v-subheader>Self Provided Contact Details</v-subheader>
        <v-text-field
          outlined
          readonly
          label="Email"
          :value="ownerDirty.contact_details.investor_contact.email"
        />
        <v-text-field
          outlined
          readonly
          label="Phone Number"
          :value="ownerDirty.contact_details.investor_contact.phone_number"
        />
      </div>

      <div v-if="ownerDirty.contact_details.bank">
        <v-subheader>Self Provided Bank Details (Read Only)</v-subheader>
        <v-text-field
          outlined
          readonly
          label="Bank Name"
          :value="ownerDirty.contact_details.bank.name"
        />
        <v-text-field
          outlined
          readonly
          label="Bank Address"
          :value="ownerDirty.contact_details.bank.address"
        />
        <v-text-field
          outlined
          readonly
          label="Official Name on Bank Account"
          :value="ownerDirty.contact_details.bank.official_name"
        />
        <v-text-field
          outlined
          readonly
          label="Official Address on Bank Account"
          :value="ownerDirty.contact_details.bank.official_address"
        />
        <v-text-field
          outlined
          readonly
          label="Bank IBAN / Account Number"
          :value="ownerDirty.contact_details.bank.iban"
        />
        <v-text-field
          outlined
          readonly
          label="Routing / ABA"
          :value="ownerDirty.contact_details.bank.routing_number"
        />
        <v-text-field
          outlined
          readonly
          label="BIC / Swift"
          :value="ownerDirty.contact_details.bank.bic"
        />
      </div>

      <!--<editor-fold desc="Contact Details">-->
      <v-subheader>Contact Details</v-subheader>
      <v-text-field
        v-model="ownerDirty.contact_details.email"
        v-validate="'required|email'"
        name="contact_email"
        label="Email"
        outlined
        data-vv-as="email"
        :error-messages="veeErrors.collect('contact_email')"
      />

      <v-text-field
        v-model="ownerDirty.contact_details.phone"
        v-validate="'required'"
        name="contact_phone"
        label="Phone number"
        outlined
        data-vv-as="phone"
        :error-messages="veeErrors.collect('contact_phone')"
      />

      <v-text-field
        v-model="ownerDirty.contact_details.address"
        v-validate="'required'"
        name="contact_address"
        label="Address"
        outlined
        data-vv-as="address"
        :error-messages="veeErrors.collect('contact_address')"
      />

      <v-text-field
        v-model="ownerDirty.contact_details.city"
        v-validate="'required'"
        name="contact_city"
        label="City"
        outlined
        data-vv-as="city"
        :error-messages="veeErrors.collect('contact_city')"
      />

      <v-text-field
        v-model="ownerDirty.contact_details.post_code"
        v-validate="'required'"
        name="contact_post_code"
        label="Post Code"
        outlined
        data-vv-as="post code"
        :error-messages="veeErrors.collect('contact_post_code')"
      />

      <v-text-field
        v-model="ownerDirty.contact_details.state"
        name="contact_state"
        label="State"
        outlined
        data-vv-as="state"
      />
      <!--</editor-fold>-->

      <v-switch
        v-model="ownerDirty.has_user"
        :disabled="userSwitchDisabled"
        label="User Account"
      />

      <div v-if="ownerDirty.has_user">
        <v-subheader>User Account Details</v-subheader>
        <v-text-field
          v-model="ownerDirty.user.email"
          v-validate="'required|email'"
          name="user_email"
          type="email"
          label="Email"
          placeholder="email@email.com"
          autocomplete="new"
          outlined
          data-vv-as="email"
          :error-messages="veeErrors.collect('user_email')"
        />
        <v-text-field
          ref="password"
          v-model="ownerDirty.user.password"
          v-validate="'min:4'"
          name="password"
          type="password"
          label="Password"
          placeholder="Enter password"
          autocomplete="new-password"
          outlined
          :error-messages="veeErrors.collect('password')"
        />
        <v-text-field
          v-model="ownerDirty.user.password_confirmation"
          v-validate="'confirmed:password'"
          type="password"
          name="password_confirmation"
          label="Confirm Password"
          placeholder="Confirm password"
          outlined
          data-vv-as="password confirmation"
          :error-messages="veeErrors.collect('password_confirmation')"
        />
      </div>
    </v-card-text>
    <!--</editor-fold>-->

    <!--<editor-fold desc="DialogActions">-->
    <template slot="dialogActions">
      <v-btn
        v-if="ownerDirty.id && $can('delete', 'Owner')"
        small
        outlined
        color="error"
        @click="toggleConfirmationDialog(true)"
      >
        <v-icon
          left
          small
        >
          delete
        </v-icon>
        Delete
      </v-btn>
      <v-spacer />
      <v-btn
        text
        color="grey"
        @click="modalVisible = false"
      >
        Cancel
      </v-btn>
      <v-btn
        color="primary"
        :disabled="submitting"
        @click="submit"
      >
        <v-icon
          small
          left
        >
          check
        </v-icon>
        Save
      </v-btn>
    </template>
    <!--</editor-fold>-->

    <confirm-delete-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      text="Are you sure you want to delete this yacht owner?"
      @confirmed="deleteOwner"
    />
  </content-dialog>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import { mapState } from 'vuex'
import Owner from '@/models/Owner'
import ContentDialog from '@/components/helpers/Dialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'

export default {
    name: 'OwnerEditDialog',
    components: {
      ContentDialog,
      ConfirmDeleteDialog
    },
    mixins: [deleteConfirmationMixin],
    props: {
      visible: {
        type: Boolean,
        default: false
      },
      owner: {
        type: Object,
        default: () => Owner.emptyModel
      }
    },
    data() {
      return {
        ownerDirty: cloneDeep(this.owner),
        submitting: false
      }
    },
    computed: {
      modalVisible: {
        get: function () {
          return this.visible
        },
        set: function (value) {
          this.$emit('update:visible', value)
        }
      },
      userSwitchDisabled() {
        return this.ownerDirty.id && this.ownerDirty.user !== null && !!this.ownerDirty.user.id
      },
      ...mapState('settings', ['countries'])
    },
    watch: {
      owner() {
        this.ownerDirty = cloneDeep(this.owner)
      },
      'ownerDirty.has_user': function (newVal) {
        if (newVal && this.ownerDirty.user === null) {
          this.ownerDirty.user = {
            name: null,
            password: null
          }
        }
      }
    },
    methods: {
      async submit() {
        let validation = await this.$validator.validate()

        if (!validation) return

        this.save()
      },
      async save() {
        this.submitting = true

        let formData = {
          owner: {
            name: this.ownerDirty.name,
            contact_details: {
              email: this.ownerDirty.contact_details.email,
              phone: this.ownerDirty.contact_details.phone,
              address: this.ownerDirty.contact_details.address,
              city: this.ownerDirty.contact_details.city,
              post_code: this.ownerDirty.contact_details.post_code,
              state: this.ownerDirty.contact_details.state
            },
            is_company: this.ownerDirty.is_company,
            company_details: this.ownerDirty.company_details,
            country_id: this.ownerDirty.country_id
          },
          has_user: this.ownerDirty.has_user,
          user: this.ownerDirty.has_user ? {
            id: this.ownerDirty.user_id,
            email: this.ownerDirty.user.email,
            password: this.ownerDirty.user.password || undefined,
            password_confirmation: this.ownerDirty.user.password_confirmation || undefined
          } : undefined
        }

        try {
          let owner = await Owner.api.save(formData, this.ownerDirty.id)

          this.$store.dispatch('alert', {
            'text': 'Yacht Owner saved',
            'color': 'success'
          })
          this.modalVisible = false
          this.$emit(this.ownerDirty.id ? 'owner:updated' : 'owner:created', owner)
        } catch (e) {
          if (e.response && e.response.status === 422) {
            this.$store.dispatch('alert', {
              'text': e.response.data.errors[Object.keys(e.response.data.errors)[0]][0],
              'color': 'error'
            })
          } else {
            this.$store.dispatch('alert', {
              'text': 'Something went wrong',
              'color': 'error'
            })
          }

          throw e
        } finally {
          this.submitting = false
        }
      },
      async deleteOwner() {
        this.deleting = true

        try {
          await Owner.api.destroy(this.ownerDirty.id)

          this.$store.dispatch('alert', {
            'text': 'Owner deleted',
            'color': 'info'
          })
          this.modalVisible = false
          this.$emit('owner:deleted', this.ownerDirty.id)
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.deleteConfirmationDialogVisible = false
          this.deleting = false
        }
      }
    }
  }
</script>
